import { useField, useFormikContext } from 'formik';
import { motion } from 'framer-motion';
import { LegacyRef, useEffect, useRef, useState } from 'react';
import { FaClock } from 'react-icons/fa6';
import {
  TIME_PICKER_HOURS,
  TIME_PICKER_MERIDIANS,
  TIME_PICKER_MINUTES,
} from '../../../constants/settings';
import useUserClickObserver from '../../../hooks/useUserClickObserver';
import {
  convertTimeTo12Hrs,
  convertTimeTo24Hrs,
} from '../../../utils/timeAndDate';
import TimePickerButton from '../Buttons/TimePickerButton';

interface IAcademiaDatePicker {
  placeholder: string;
  label: string;
  name: string;
  isDisabled?: boolean;
  isRequired?: boolean;
  defaultTime?: string;
}

const AcademiaTimePicker = ({
  name,
  label,
  placeholder,
  isDisabled,
  isRequired,
  defaultTime,
}: IAcademiaDatePicker) => {
  // integration of react hooks here
  const [time, setTime] = useState<string>('--:-- --');
  const [selectedHour, setSelectedHour] = useState<string>('--');
  const [selectedMinute, setSelectedMinute] = useState<string>('--');
  const [selectedMeridian, setSelectedMeridian] = useState<string>('--');

  const [showTimePicker, setShowTimePicker] = useState<boolean>(false);

  const hourRef = useRef<HTMLDivElement>();

  // integration of formik hooks here
  const [field, meta] = useField(name);
  const { setFieldValue } = useFormikContext();

  // integration of custom hooks here
  const selectRef = useUserClickObserver(setShowTimePicker);

  // setting default value for hours, minutes & meridian here
  useEffect(() => {
    if (!defaultTime) {
      const currentDate = new Date();

      const { hours, minutes, meridian } = convertTimeTo12Hrs(currentDate);

      setSelectedHour(hours.toString().padStart(2, '0'));
      setSelectedMinute(minutes.toString().padStart(2, '0'));
      setSelectedMeridian(meridian);
    } else {
      const [defaultHrs, rest] = defaultTime.split(':');
      const defaultMins = rest.split(' ')[0];
      const defaultMeridian = rest.split(' ')[1];

      setSelectedHour(defaultHrs.padStart(2, '0'));
      setSelectedMinute(defaultMins.padStart(2, '0'));
      setSelectedMeridian(defaultMeridian);
    }
  }, [defaultTime]);

  // setting time in the input field here
  useEffect(() => {
    setTime(`${selectedHour}:${selectedMinute} ${selectedMeridian}`);
    setFieldValue(
      name,
      convertTimeTo24Hrs(
        `${selectedHour}:${selectedMinute} ${selectedMeridian}`
      )
    );
  }, [selectedHour, selectedMinute, selectedMeridian, name, setFieldValue]);

  // rendering custom time picker component here
  return (
    <div>
      <label
        htmlFor={`input-${name}`}
        title={isRequired ? 'This field is required' : ''}
        className='block mb-1 text-legal-dark-blue font-medium text-base'
      >
        {label} {isRequired && <span className='text-base-red'>*</span>}
      </label>

      <motion.div
        tabIndex={0}
        initial={{ x: 0 }}
        animate={
          meta.touched && meta.error
            ? {
                x: [0, -10, 0, 10, 0],
                transition: {
                  duration: 0.1,
                  repeat: 2,
                },
              }
            : { x: 0 }
        }
        ref={selectRef as LegacyRef<HTMLDivElement>}
        onFocus={() => setShowTimePicker(true)}
        onBlur={() => setShowTimePicker(false)}
        className='relative'
      >
        <div className='group cursor-pointer'>
          <input
            tabIndex={-1}
            id={`input-${name}`}
            type='text'
            className={`border-2 outline-none rounded-md w-full p-3 text-legal-dark-blue font-semibold duration-300 text-base disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer group-active:scale-[0.98] ${
              meta.touched && meta.error
                ? 'border-base-red'
                : 'border-legal-dark-blue'
            }`}
            value={time}
            onChange={field.onChange}
            placeholder={placeholder}
            disabled={isDisabled}
            autoComplete='off'
            readOnly
          />

          <div className='absolute top-1/2 -translate-y-1/2 right-5'>
            <FaClock className='size-5 text-legal-dark-blue group-active:scale-90 duration-300' />
          </div>
        </div>

        <motion.div
          initial={{ height: 0 }}
          animate={showTimePicker ? { height: '280px' } : { height: 0 }}
          className='flex gap-[2px] absolute bg-base-white shadow-academia-sm rounded-md mt-1 overflow-hidden z-30'
        >
          <div
            ref={hourRef as LegacyRef<HTMLDivElement>}
            className='h-full overflow-auto no-scrollbar'
          >
            {TIME_PICKER_HOURS.map((hour) => (
              <TimePickerButton
                key={hour}
                selectedHrOrMin={selectedHour}
                hourOrMin={hour}
                onClick={() => setSelectedHour(hour)}
              />
            ))}
          </div>

          <div className='h-full overflow-auto no-scrollbar'>
            {TIME_PICKER_MINUTES.map((minute) => (
              <TimePickerButton
                key={minute}
                selectedHrOrMin={selectedMinute}
                hourOrMin={minute}
                onClick={() => setSelectedMinute(minute)}
              />
            ))}
          </div>

          <div className='h-full overflow-auto no-scrollbar'>
            {TIME_PICKER_MERIDIANS.map((mer) => (
              <TimePickerButton
                key={mer}
                selectedHrOrMin={selectedMeridian}
                hourOrMin={mer}
                onClick={() => setSelectedMeridian(mer)}
              />
            ))}
          </div>
        </motion.div>
      </motion.div>

      <motion.label
        initial={{ y: -20, opacity: 0, display: 'none' }}
        animate={
          meta.touched && meta.error
            ? { y: 0, opacity: 1, display: 'block' }
            : { y: -20, opacity: 0, transitionEnd: { display: 'none' } }
        }
        htmlFor={`input-${name}`}
        className='block mt-1 text-base-red text-base'
      >
        {meta.error}
      </motion.label>
    </div>
  );
};

export default AcademiaTimePicker;
